import * as types from '../mutation-types'

import Vapi from 'vuex-rest-api'
// import moment from 'moment'
import { transformPlan } from '../../utils'
import Vue from 'vue'
import Ajv from 'ajv'
import schema from '@/dialpan_schema.json'
import router from '@/router'
import uuid from 'uuid'
import { defaults, merge, take, orderBy, omit, cloneDeep } from 'lodash'

const ajv = new Ajv({coerceTypes: true, removeAdditional: true})
ajv.addSchema(schema, 'plan')
// const today = moment(new Date()).format('YYYY-MM-DD')

function defaultState () {
  return {
    status: null,
    tts: null,
    all: [],
    current: null,
    voices2: [],
    peers: [],
    currentVariables: null
  }
}

function defaultCampaign (state) {
  const num = state.all.length + 1
  return {
    name: `Campaign ${num}`,
    concurrentCalls: 10,
    waitTime: 20,
    retryTime: 15,
    contactRate: 99,
    sipPeer: '',
    dialPrefix: '',
    answerMachineDetection: true,
    answerMachineDetectionHangUp: true,
    callerId: '',
    clientId: '',
    isdnRejectCodes: '',
    routeGroup: '',
    organization_id: 1,
    subscriber_db_id: null,
    maxAttempts: 3,
    minHour: null,
    open: null,
    maxHour: null,
    startDate: null,
    endDate: null,
    country: null,
    cps: 0,
    plan: {
      initialStep: {
        type: 'TTS',
        title: '',
        step: null,
        input: null,
        voice: null,
        transition: 0
      }
    }
  }
}

const module = new Vapi({
  baseURL: Vue.$backendBaseURL,
  state: defaultState()
})
.get({
  action: 'listCampaigns',
  property: 'all',
  path: ({ orgId, query = '' }) => `/orgs/${orgId}/campaigns${query}`
})
.get({
  action: 'getCampaign',
  property: 'current',
  path: ({ orgId, id }) => `/orgs/${orgId}/campaigns/${id}`
})
.get({
  action: 'listPeers',
  property: 'peers',
  path: '/peers'
})
.post({
  action: 'createCampaign',
  property: 'current',
  path: ({ orgId }) => `/orgs/${orgId}/campaigns`,
  onError: (state, error) => {
    state.pending.current = false
    state.error.current = error
    return error
  }
})
.put({
  action: 'updateCampaign',
  property: 'current',
  path: ({ orgId, id }) => `/orgs/${orgId}/campaigns/${id}`,
  onError: (state, error) => {
    state.pending.current = false
    state.error.current = error
    return error
  }
})
.patch({
  action: 'patchCampaign',
  property: 'current',
  path: ({ orgId, id }) => `/orgs/${orgId}/campaigns/${id}`
})
.get({
  action: 'listVoices2',
  property: 'voices2',
  path: '/voices2'
})
.get({
  action: 'getCampaignReportAnswers',
  property: 'reportAnswers',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/reports/answers`
})
.get({
  action: 'getCampaignReportMinutes',
  property: 'reportMinutes',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/reports/minutes`
})
.get({
  action: 'getCampaignReportDisposition',
  property: 'reportDisposition',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/reports/disposition`
})
.get({
  action: 'getCampaignReportAttempts',
  property: 'reportAttempts',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/reports/attempts`
})
.patch({
  action: 'patchCampaignStatus',
  property: 'status',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/status`
})
.patch({
  action: 'patchCampaignTTS',
  property: 'tts',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/TTS`
})
.put({
  action: 'updateCampaignTTS',
  property: 'current',
  path: ({ orgId, campaignId }) => `/orgs/${orgId}/campaigns/${campaignId}/TTS`
})
.getStore()

const state = {
  duplicate: false
}

const getters = {
  lastCampaigns (state) {
    return orderBy(take(state.all, 50), ['id'], ['desc'])
  },
  openCampaigns (state) {
    if (Array.isArray(state.all)) {
      return state.all.filter(c => c.open && c.state !== 'draft' && c.state !== 'finished')
    }
  },
  subscriberDbId (state) {
    if (state.current) return state.current.subscriber_db_id
  },
  initialStep (state) {
    if (state.current && state.current.plan) return state.current.plan.initialStep
  },
  voices (state) {
    return state.voices
  },
  voices2 (state) {
    return state.voices2.map(voice => {
      let languageCode = voice.languageCode
      switch (voice.languageCode) {
        case 'cmn-CN':
          languageCode = 'zh-CN'
          break
        case 'arb':
          languageCode = 'ar'
          break
      }
      return Object.assign({}, voice, {languageCode})
    })
  },
  peers (state) {
    return state.peers
  },
  campaignById (state) {
    return function (id) {
      return defaults(state.all.find(item => item.id === id),
        {
          endDate: new Date().toISOString().substring(0, 10),
          maxAttempts: 5,
          concurrentCalls: 99
        }
      )
    }
  },
  variables (state) {
    return state.currentVariables
  },

  // pagination getters
  page (state) {
    return state.page
  },
  size (state) {
    return state.size
  }
}

function getAllAudios (initStep) {
  const sounds = []
  const audioProps = ['preAudio', 'postAudio', 'audio', 'repeatPreAudio', 'invalidAudio']
  const traverse = (obj) => {
    const nextObj = obj['step'] || obj['input']
    if (nextObj) {
      traverse(nextObj)
    }
    if (obj['choices'] && Array.isArray(obj['choices'])) {
      obj['choices'].forEach((v) => {
        if (v['step']) traverse(v['step'])
      })
    }
    for (let k of audioProps) {
      if (!obj[k]) { continue }
      const snd = sounds.find(s => s.uuid === obj[k])
      if (snd) {
        obj[k] = snd.newUuid
      } else {
        const tmpsnd = {uuid: obj[k], newUuid: uuid()}
        obj[k] = tmpsnd.newUuid
        sounds.push(tmpsnd)
      }
    }
  }
  traverse(initStep)
  return sounds
}

function mapVoiceToVoice2 (initStep) {
  const traverse = (obj) => {
    const nextObj = obj['step'] || obj['input']
    if (nextObj) {
      traverse(nextObj)
    }
    if (obj['choices'] && Array.isArray(obj['choices'])) {
      obj['choices'].forEach((v) => {
        if (v['step']) traverse(v['step'])
      })
    }
    const voiceId = obj['voice']
    if (!voiceId) { return }
    obj['voice2'] = convertVoice(voiceId)
  }
  traverse(initStep)
}

function convertVoice (VoiceId) {
  const id = parseInt(VoiceId) - 1
  if (id < 0 || id > 15) {
    return null
  }
  const voice = [
    ['Miguel', 'es-US'],
    ['Conchita', 'es-ES'],
    ['Penelope', 'en-US'],
    ['Enrique', 'es-ES'],
    ['Ricardo', 'pt-BR'],
    ['Vitoria', 'pt-BR'],
    ['Cristiano', 'pt-PT'],
    ['Ines', 'pt-PT'],
    ['Mia', 'es-MX'],
    ['Lucia', 'it-IT'],
    ['Lupe', 'es-US'],
    ['Joanna', 'en-US'],
    ['Kendra', 'en-US'],
    ['Kimberly', 'en-US'],
    ['Joey', 'en-US'],
    ['Matthew', 'en-US']
  ][id]
  return {
    provider: 'aws',
    voiceCode: voice[0],
    languageCode: voice[1],
    voiceName: voice[0],
    premium: false
  }
}

const actions = {
  updateCampaignPlan ({ commit, rootState }) {
    let tree = rootState.dialplan.tree
    let plan = transformPlan(tree)
    if (ajv.validate('plan', plan)) commit(types.SET_CAMPAIGN_PLAN, { plan })
  },
  cloneCampaign ({commit, state, dispatch}, {campaignId}) {
    const campaign = state.all.data.find((c) => c.id === campaignId)
    if (!campaign) return

    var step = cloneDeep(campaign.plan.initialStep)
    mapVoiceToVoice2(step)
    step = omit(cloneDeep(step), 'voice')
    const audios = getAllAudios(step)
    const newcampaign = omit(cloneDeep(campaign), 'id')
    newcampaign.plan.initialStep = step
    commit(types.SET_CURRENT_CAMPAIGN, { campaign: newcampaign })
    commit(types.SET_DIALPLAN_FROM_STEP, { step })
    dispatch('getAudiosFromDuplicate', {audios})
    router.push({ path: `/admin/campaigns/new` })
  },
  reset ({ commit, rootState }) {
    commit(types.RESET_CURRENT_CAMPAIGN)
    commit(types.RESET_CAMPAIGNS)
  }
}

// Save reference to the Vapi-generated listCampaigns action so we can wrap it
const _vapiListCampaigns = module.actions && module.actions.listCampaigns ? module.actions.listCampaigns : null

// Wrap/override the listCampaigns action to update pagination in the store
// before delegating to the original Vapi action. This keeps callers unchanged
// (they still call `listCampaigns`) while ensuring the store holds the
// current page/size each time a fetch is made.
actions.listCampaigns = function ({ commit, dispatch, state, rootState, getters }, payload) {
  let page = state.page || 1
  let size = state.size || 10

  commit('SET_CAMPAIGN_PAGINATION', { page, size })

  if (_vapiListCampaigns) {
    // Call the original Vapi-generated action with the same context and payload
    return _vapiListCampaigns({ commit, dispatch, state, rootState, getters }, payload)
  }
  return Promise.resolve()
}

const mutations = {
  [types.SET_CAMPAIGN_PLAN] (state, { plan }) {
    Vue.set(state.current, 'plan', plan)
  },

  // pagination mutations
  SET_CAMPAIGN_PAGINATION (state, { page, size }) {
    state.page = page
    state.size = size
  },
  SET_CAMPAIGN_PAGE (state, page) {
    state.page = page
  },
  SET_CAMPAIGN_SIZE (state, size) {
    state.size = size
  },

  [types.SET_CURRENT_CAMPAIGN] (state, { campaign }) {
    state.current = campaign
  },
  [types.SET_DB_VARIABLES] (state, { value }) {
    state.currentVariables = value
  },
  [types.RESET_CAMPAIGNS] (state) {
    Object.assign(state, defaultState())
  },
  [types.RESET_CURRENT_CAMPAIGN] (state) {
    state.current = defaultCampaign(state)
    state.pending = {
      current: false
    }
    state.error = {
      current: null
    }
  }
}

export default merge(module, {
  state,
  getters,
  actions,
  mutations
})



// WEBPACK FOOTER //
// ./src/store/modules/campaign.js